import {gql} from "@apollo/client";

import {ServiceDefinition, ServiceGroup, ServiceTag} from "@/lib/types/checks.ts";
import {GraphQLResponse} from "@/lib/types/common.ts";
import {FGM_METRICS_EXPLORER_RESULT_FIELDS} from "@/lib/types/fragments.metricsExplorer.ts";
import {
  ChangelogEntry,
  DashboardChartWidget,
  DashboardDefinition,
  DashboardMetrics,
  DashboardServiceInfo,
} from "@/lib/types/website.ts";

export const GQL_CONTACT = gql`
  mutation Contact($data: ContactInput!) {
    response: contact(data: $data) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface DashboardDefinitionResult {
  dashboardDef: DashboardDefinition;
  dashboardDefs: DashboardDefinition[];
}

export type UpdateDashboardDefinitionResult = GraphQLResponse<DashboardDefinition>;

export const GQL_GET_DEFAULT_DASHBOARD = gql`
  query dashboardDefinition {
    dashboardDef: dashboardDefinition {
      id
    }
  }
`;

export const GQL_DASHBOARD_DEFINITION = gql`
  query dashboardDefinition($id: Int) {
    dashboardDef: dashboardDefinition(id: $id) {
      ...DashboardDefinitionFields
    }
    dashboardDefs: dashboardDefinitions {
      id
      name
      ordering
    }
  }
`;

export interface DashboardStatsResult {
  dashboardMetrics: DashboardMetrics;
  dashboardServiceInfo: DashboardServiceInfo;
  serviceDefinitions: ServiceDefinition[];
  monitoringServiceTypes: ServiceGroup[];
  tags: ServiceTag[];
  dashboardDefs: DashboardDefinition[];
}

export const GQL_DASHBOARD_STATS = gql`
  query dashboardMetrics($dashboard_def: DashboardDefinitionInput!, $limit: Int) {
    dashboardMetrics(dashboard_def: $dashboard_def) {
      global_metrics {
        checks_are_filtered
        checks {
          total
          paused
          down
          maintenance
        }
        outages {
          days_1
          days_7
          days_30
          last
        }
        uptime {
          uptime_pct
          uptime_sla
          downtime_secs
        }
        response_time {
          API {
            response_time_avg
            response_time_max
            response_time_sla
          }
          HTTP {
            response_time_avg
            response_time_max
            response_time_sla
          }
          RUM2 {
            response_time_avg
            response_time_max
            response_time_sla
          }
          TRANSACTION {
            response_time_avg
            response_time_max
            response_time_sla
          }
        }
      }
      latest_outages {
        ...AlertListItemFields
      }
    }
    dashboardServiceInfo(dashboard_def: $dashboard_def, limit: $limit) {
      service_info {
        id
        name
        cached_last_down_alert_at
        cached_last_up_alert_at
        cached_response_time
        cached_state_is_up
        cached_uptime_grade
        group_check_services {
          id
        }
        group_check_tag_ids
        is_paused
        is_under_maintenance
        monitoring_service_type
        msp_address
        msp_response_time_sla
        msp_uptime_sla
        msp_threshold
        device {
          address
        }
      }
      service_total
      service_performance_metrics {
        service_id
        uptime_24h
        response_time_datapoints {
          date
          response_time
        }
      }
    }
    serviceDefinitions {
      service_type
      performance_data_type
      performance_data_type_display
    }
    monitoringServiceTypes {
      ...MonitoringGroupFields
    }
    tags {
      id
      tag
      color
      color_hex
    }
    dashboardDefs: dashboardDefinitions {
      id
      name
    }
  }
`;

export const GQL_DASHBOARD_SAVE = gql`
  mutation dashboardSave($dashboard_def: DashboardDefinitionInput!) {
    response: dashboardSave(dashboard_def: $dashboard_def) {
      ... on SaveDashboardSuccess {
        status
        data {
          ...DashboardDefinitionFields
        }
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export const GQL_DASHBOARD_DELETE = gql`
  mutation dashboardDelete($dashboard_id: Int!) {
    response: dashboardDelete(dashboard_id: $dashboard_id) {
      ... on BaseResponse {
        status
      }
      ... on ErrorResponse {
        status
        message
      }
    }
  }
`;

export interface ChangelogResults {
  changelog: ChangelogEntry[];
}

export const GQL_CHANGELOG = gql`
  query Changelog {
    changelog {
      date
      title
      content
      isPublished
    }
  }
`;

export interface DashboardChartsData {
  dashboardCharts: DashboardChartWidget[];
}

export const GQL_DASHBOARD_CHARTS = gql`
  query DashboardCharts($charts: [DashboardChartInput!]!) {
    dashboardCharts(charts: $charts) {
      view_id
      view_name
      time_range
      chart_type
      status
      start_date
      end_date
      granularity
      computed_at
      check_names {
        check_id
        name
      }
      error_code
      error_message
      result {
        ...MetricsExplorerResultFields
      }
    }
  }
  ${FGM_METRICS_EXPLORER_RESULT_FIELDS}
`;
